import { useEffect, useRef, useState } from 'react';
import { SlidersHorizontal, RotateCcw, Check, ChevronDown, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import { CreditRating, Priority } from '../data/mockData';
import { SearchableSelect } from './AddLeadDialog';
import { cn } from '../../lib/utils';
import apiService from '../../services/apiService';
import { Stage } from './TopBar';

export interface FilterState {
  search: string;
  afLead: string;
  relationshipManager: string;
  partnerOwner: string;
  creditRating: string;
  city: string;
  productType: string;
  dealValueMin: number;
  priority: string;
  pipelineStatus: string;
  loginStatus: string;
  mraStatus: string;
  tsSignedStatus: string;
  dealType: string;
  companySize: string;
}

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  filterOptions: {
    afLeads: string[];
    relationshipManagers: string[];
    partnerOwners: string[];
    cities: string[];
    productTypes: string[];
  };
  activeStage: Stage;
  statusOptions: Array<{
    id: string | number;
    name: string;
  }>;
}

interface PartnerOption {
  id: string;
  name: string;
}

interface AdminUserOption {
  id: string;
  name: string;
}

const creditRatings: CreditRating[] = [
  'AAA',
  'AA+',
  'AA',
  'AA-',
  'A+',
  'A',
  'A-',
  'BBB+',
  'BBB',
  'BBB-',
  'BB+',
  'BB',
  'BB-',
  'B+',
  'B',
  'B-',
  'CCC+',
  'CCC',
  'CCC-',
  'CC',
  'C',
  'D',
];
const priorities: Priority[] = ['High', 'Medium', 'Low'];
const documentStatuses = ['Pending', 'Signed'];
const dealTypes = ['Leasing', 'Hybrid', 'Smart EPP'];
// const companySizes = ['Small', 'Mid', 'Large'];

export function FilterDrawer({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  filterOptions,
  activeStage,
  statusOptions,
}: FilterDrawerProps) {
  const [partnerOptions, setPartnerOptions] = useState<PartnerOption[]>([]);
  const [isPartnerDropdownOpen, setIsPartnerDropdownOpen] = useState(false);
  const partnerDropdownRef = useRef<HTMLDivElement | null>(null);
  const [afLeadOptions, setAfLeadOptions] = useState<AdminUserOption[]>([]);
  const [isAfLeadDropdownOpen, setIsAfLeadDropdownOpen] = useState(false);
  const afLeadDropdownRef = useRef<HTMLDivElement | null>(null);
  const [isCreditRatingDropdownOpen, setIsCreditRatingDropdownOpen] = useState(false);
  const creditRatingDropdownRef = useRef<HTMLDivElement | null>(null);
  const [afLeadSearch, setAfLeadSearch] = useState('');
  const [partnerSearch, setPartnerSearch] = useState('');
  const dealValueTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const filtersRef = useRef(filters);
  const [dealValueInput, setDealValueInput] = useState("");

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'search') return false;
    if (key === 'dealValueMin') return (value as number) > 0;
    return value !== '' && value !== 'all';
  }).length;

  const updateFilter = (key: keyof FilterState, value: string | number) => {
    onFilterChange({ ...filtersRef.current, [key]: value });
  };

  const clearFilters = () => {
    onFilterChange({
      search: filters.search, afLead: '', relationshipManager: '', partnerOwner: '', creditRating: '',
      city: '', productType: '', dealValueMin: 0, priority: '', pipelineStatus: '',
      loginStatus: '', mraStatus: '', tsSignedStatus: '', dealType: '', companySize: '',
    });
  };

  // const handleDealValueChange = (rawValue: string) => {
  //   const num = parseFloat(rawValue) || 0;
  //   updateFilter('dealValueMin', num);
  // };

  const handleDealValueChange = (rawValue: string) => {
    setDealValueInput(rawValue); // instant UI update

    if (dealValueTimeoutRef.current) {
      window.clearTimeout(dealValueTimeoutRef.current);
    }

    dealValueTimeoutRef.current = setTimeout(() => {
      const num = parseFloat(rawValue);
      updateFilter("dealValueMin", isNaN(num) ? 0 : num);
    }, 1000);
  };
  const handleSelectFilterChange = (key: keyof FilterState, value: string) => {
    updateFilter(key, value === 'all' ? '' : value);
  };

  useEffect(() => {
    if (activeStage === 'active-portfolio' && filters.pipelineStatus) {
      onFilterChange({ ...filtersRef.current, pipelineStatus: '' });
    }
  }, [activeStage, filters.pipelineStatus, onFilterChange]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    return () => {
      if (dealValueTimeoutRef.current) {
        window.clearTimeout(dealValueTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchPartners = async () => {
      if (!isOpen) return;
      try {
        const response = await apiService.get('/customers/get-partner-company-list') as { data?: unknown };
        const payload: unknown = response?.data;
        const payloadObject = (payload && typeof payload === 'object') ? payload as Record<string, unknown> : undefined;
        const nestedData = (payloadObject?.data && typeof payloadObject.data === 'object')
          ? payloadObject.data as Record<string, unknown>
          : undefined;

        const rawList = Array.isArray(payload)
          ? payload
          : Array.isArray(payloadObject?.data)
            ? payloadObject.data
            : Array.isArray(payloadObject?.partners)
              ? payloadObject.partners
              : Array.isArray(payloadObject?.partnerCompanies)
                ? payloadObject.partnerCompanies
                : Array.isArray(payloadObject?.partner_company_list)
                  ? payloadObject.partner_company_list
                  : Array.isArray(nestedData?.partners)
                    ? nestedData.partners
                    : Array.isArray(nestedData?.partnerCompanies)
                      ? nestedData.partnerCompanies
                      : Array.isArray(nestedData?.partner_company_list)
                        ? nestedData.partner_company_list
                        : [];

        const options: PartnerOption[] = rawList
          .map((item: unknown) => {
            if (!item || typeof item !== 'object') return null;
            const option = item as Record<string, unknown>;
            const nameRaw = option.partnerCompanyName
              ?? option.partner_company_name
              ?? option.name
              ?? option.companyName;
            const idRaw = option.partner_id
              ?? option.partnerId
              ?? option.id
              ?? option.company_id;
            const name = typeof nameRaw === 'string' ? nameRaw.trim() : '';
            const id = idRaw === undefined || idRaw === null ? '' : String(idRaw).trim();
            if (!name || !id) return null;
            return { id, name };
          })
          .filter((opt: PartnerOption | null): opt is PartnerOption => opt !== null);

        setPartnerOptions(options);
      } catch (error) {
        console.error('Error fetching partner list for filters:', error);
      }
    };

    void fetchPartners();
  }, [isOpen]);

  useEffect(() => {
    const fetchAfLeads = async () => {
      if (!isOpen) return;
      try {
        const response = await apiService.get('/customers/get-admin-user-list-active') as { data?: unknown };
        const payload: unknown = response?.data;
        const payloadObject = (payload && typeof payload === 'object') ? payload as Record<string, unknown> : undefined;
        const nestedData = (payloadObject?.data && typeof payloadObject.data === 'object')
          ? payloadObject.data as Record<string, unknown>
          : undefined;

        const rawList = Array.isArray(payload)
          ? payload
          : Array.isArray(payloadObject?.data)
            ? payloadObject.data
            : Array.isArray(payloadObject?.users)
              ? payloadObject.users
              : Array.isArray(payloadObject?.adminUsers)
                ? payloadObject.adminUsers
                : Array.isArray(nestedData?.users)
                  ? nestedData.users
                  : Array.isArray(nestedData?.adminUsers)
                    ? nestedData.adminUsers
                    : [];

        const options: AdminUserOption[] = rawList
          .map((item: unknown) => {
            if (!item || typeof item !== 'object') return null;
            const option = item as Record<string, unknown>;
            const nameRaw = option.leadRM ?? option.name ?? option.fullName ?? option.user_name;
            const idRaw = option.id ?? option.user_id ?? option.af_lead_id ?? option.lead_rm_id;
            const name = typeof nameRaw === 'string' ? nameRaw.trim() : '';
            const id = idRaw === undefined || idRaw === null ? '' : String(idRaw).trim();
            if (!name || !id) return null;
            return { id, name };
          })
          .filter((opt: AdminUserOption | null): opt is AdminUserOption => opt !== null);

        setAfLeadOptions(options);
      } catch (error) {
        console.error('Error fetching AF lead list for filters:', error);
      }
    };

    void fetchAfLeads();
  }, [isOpen]);

  useEffect(() => {
    if (!isPartnerDropdownOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (partnerDropdownRef.current && !partnerDropdownRef.current.contains(target)) {
        setIsPartnerDropdownOpen(false);
        setPartnerSearch('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isPartnerDropdownOpen]);

  useEffect(() => {
    if (!isAfLeadDropdownOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (afLeadDropdownRef.current && !afLeadDropdownRef.current.contains(target)) {
        setIsAfLeadDropdownOpen(false);
        setAfLeadSearch('');
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isAfLeadDropdownOpen]);

  useEffect(() => {
    if (!isCreditRatingDropdownOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (creditRatingDropdownRef.current && !creditRatingDropdownRef.current.contains(target)) {
        setIsCreditRatingDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isCreditRatingDropdownOpen]);

  const selectedPartnerIds = filters.partnerOwner
    ? filters.partnerOwner.split(',').filter(Boolean)
    : [];

  const allPartnersSelected =
    partnerOptions.length > 0 && selectedPartnerIds.length === partnerOptions.length;

  const selectedAfLeadIds = filters.afLead
    ? filters.afLead.split(',').filter(Boolean)
    : [];

  const allAfLeadsSelected =
    afLeadOptions.length > 0 && selectedAfLeadIds.length === afLeadOptions.length;

  const selectedCreditRatings = filters.creditRating
    ? filters.creditRating.split(',').filter(Boolean)
    : [];

  const allCreditRatingsSelected =
    creditRatings.length > 0 && selectedCreditRatings.length === creditRatings.length;

  const statusNames = statusOptions.map((status) => status.name);
  const selectedStatusName = statusOptions.find(
    (status) => String(status.id) === String(filters.pipelineStatus)
  )?.name ?? '';
  const cityOptions = Array.from(new Set(filterOptions.cities.map((city) => city.trim()).filter(Boolean)));
  const productTypeOptions = Array.from(new Set(filterOptions.productTypes.map((productType) => productType.trim()).filter(Boolean)));

  const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="space-y-1.5">
      <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );

  const ToggleChip = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => (
    <button
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border transition-all duration-150',
        active
          ? 'bg-accent/20 text-accent-foreground border-accent/50 shadow-sm'
          : 'bg-card text-foreground/80 border-border hover:border-accent/30 hover:text-foreground'
      )}
    >
      {active && <Check className="w-3 h-3" />}
      {label}
    </button>
  );

  const SectionHeader = ({ title }: { title: string }) => (
    <div className="flex items-center gap-2 pb-1">
      <span className="text-xs font-semibold text-foreground">{title}</span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[440px] p-0 flex flex-col bg-white">
        <SheetHeader className="px-5 py-3 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-accent-foreground" />
              <SheetTitle className="text-sm font-semibold text-foreground">Filters</SheetTitle>
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 bg-accent/10 text-accent-foreground rounded text-[10px] font-medium">{activeFilterCount}</span>
              )}
            </div>
            {activeFilterCount > 0 && (
              <button onClick={clearFilters} className="text-xs text-destructive hover:underline">
                Clear all
              </button>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 scrollbar-thin space-y-5">
          <div className="space-y-2">
            <SectionHeader title="Deal Value" />
            <div className="space-y-1">
              <label className="text-[10px] text-muted-foreground">Deal value more than (in Lakhs)</label>
              <Input
                type="number"
                step="0.1"
                min="0"
                // value={filters.dealValueMin || ''}
                value={dealValueInput}
                onChange={(e) => handleDealValueChange(e.target.value)}
                placeholder="e.g. 1"
                className="h-8 text-xs"
              />
            </div>
          </div>

          <div className="space-y-2">
            <SectionHeader title="Quick Filters" />
            <div className="space-y-2">
              <label className="text-[10px] text-muted-foreground">Deal Type</label>
              <div className="flex flex-wrap gap-1.5">
                {dealTypes.map((dt) => (
                  <ToggleChip key={dt} label={dt} active={filters.dealType === dt} onClick={() => updateFilter('dealType', filters.dealType === dt ? '' : dt)} />
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <SectionHeader title="Team" />
            <FilterField label="AF Lead">
              <div className="relative" ref={afLeadDropdownRef}>
                <button
                  type="button"
                  className={cn(
                    'w-full h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground',
                    'flex items-center justify-between gap-2',
                    'hover:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent'
                  )}
                  onClick={() => setIsAfLeadDropdownOpen((prev) => !prev)}
                >
                  <span className="truncate text-left">
                    {selectedAfLeadIds.length === 0
                      ? 'Select AF Leads'
                      : selectedAfLeadIds.length === afLeadOptions.length
                        ? 'All AF Leads selected'
                        : `${selectedAfLeadIds.length} AF Lead(s) selected`}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-muted-foreground shrink-0 transition-transform',
                      isAfLeadDropdownOpen ? 'rotate-180' : 'rotate-0'
                    )}
                  />
                </button>

                {isAfLeadDropdownOpen && (
                  <div className="absolute left-0 right-0 mt-1 border rounded-md p-2 bg-white shadow-md z-[60] space-y-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Search AF Leads..."
                        value={afLeadSearch}
                        onChange={(e) => setAfLeadSearch(e.target.value)}
                        className="pl-8 h-8 text-xs"
                        autoFocus
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="selectAllAfLeads"
                        checked={allAfLeadsSelected}
                        onCheckedChange={(checked) => {
                          const isChecked = checked === true;
                          if (isChecked) {
                            const allIds = afLeadOptions.map((u) => u.id);
                            updateFilter('afLead', allIds.join(','));
                          } else {
                            updateFilter('afLead', '');
                          }
                        }}
                      />
                      <label
                        htmlFor="selectAllAfLeads"
                        className="text-xs text-muted-foreground cursor-pointer"
                      >
                        Select All AF Leads
                      </label>
                    </div>

                    <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
                      {afLeadOptions
                        .filter((option) =>
                          option.name.toLowerCase().includes(afLeadSearch.toLowerCase())
                        ).length > 0 ? (
                        afLeadOptions
                          .filter((option) =>
                            option.name.toLowerCase().includes(afLeadSearch.toLowerCase())
                          )
                          .map((option) => {
                            const checked = selectedAfLeadIds.includes(option.id);
                            return (
                              <div key={option.id} className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-muted/40">
                                <Checkbox
                                  id={`aflead-${option.id}`}
                                  checked={checked}
                                  onCheckedChange={(checkedValue) => {
                                    const isChecked = checkedValue === true;
                                    let nextIds = selectedAfLeadIds;
                                    if (isChecked) {
                                      nextIds = Array.from(new Set([...selectedAfLeadIds, option.id]));
                                    } else {
                                      nextIds = selectedAfLeadIds.filter((id) => id !== option.id);
                                    }
                                    const value =
                                      nextIds.length === 0
                                        ? ''
                                        : nextIds.join(',');
                                    updateFilter('afLead', value);
                                  }}
                                />
                                <label
                                  htmlFor={`aflead-${option.id}`}
                                  className="text-xs text-foreground cursor-pointer"
                                >
                                  {option.name}
                                </label>
                              </div>
                            );
                          })
                      ) : (
                        afLeadOptions.length > 0 && afLeadSearch && (
                          <p className="text-[11px] text-muted-foreground text-center py-2">No results found</p>
                        )
                      )}
                      {afLeadOptions.length === 0 && (
                        <p className="text-[11px] text-muted-foreground">No AF Leads available.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </FilterField>
            <FilterField label="Partner Owner">
              <div className="relative" ref={partnerDropdownRef}>
                <button
                  type="button"
                  className={cn(
                    'w-full h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground',
                    'flex items-center justify-between gap-2',
                    'hover:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent'
                  )}
                  onClick={() => setIsPartnerDropdownOpen((prev) => !prev)}
                >
                  <span className="truncate text-left">
                    {selectedPartnerIds.length === 0
                      ? 'Select partners'
                      : selectedPartnerIds.length === partnerOptions.length
                        ? 'All partners selected'
                        : `${selectedPartnerIds.length} partner(s) selected`}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-muted-foreground shrink-0 transition-transform',
                      isPartnerDropdownOpen ? 'rotate-180' : 'rotate-0'
                    )}
                  />
                </button>

                {isPartnerDropdownOpen && (
                  <div className="absolute left-0 right-0 mt-1 border rounded-md p-2 bg-white shadow-md z-[60] space-y-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Search partners..."
                        value={partnerSearch}
                        onChange={(e) => setPartnerSearch(e.target.value)}
                        className="pl-8 h-8 text-xs"
                        autoFocus
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="selectAllPartners"
                        checked={allPartnersSelected}
                        onCheckedChange={(checked) => {
                          const isChecked = checked === true;
                          if (isChecked) {
                            const allIds = partnerOptions.map((p) => p.id);
                            updateFilter('partnerOwner', allIds.join(','));
                          } else {
                            updateFilter('partnerOwner', '');
                          }
                        }}
                      />
                      <label
                        htmlFor="selectAllPartners"
                        className="text-xs text-muted-foreground cursor-pointer"
                      >
                        Select All Partners
                      </label>
                    </div>

                    <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
                      {partnerOptions
                        .filter((option) =>
                          option.name.toLowerCase().includes(partnerSearch.toLowerCase())
                        ).length > 0 ? (
                        partnerOptions
                          .filter((option) =>
                            option.name.toLowerCase().includes(partnerSearch.toLowerCase())
                          )
                          .map((option) => {
                            const checked = selectedPartnerIds.includes(option.id);
                            return (
                              <div key={option.id} className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-muted/40">
                                <Checkbox
                                  id={`partner-${option.id}`}
                                  checked={checked}
                                  onCheckedChange={(checkedValue) => {
                                    const isChecked = checkedValue === true;
                                    let nextIds = selectedPartnerIds;
                                    if (isChecked) {
                                      nextIds = Array.from(new Set([...selectedPartnerIds, option.id]));
                                    } else {
                                      nextIds = selectedPartnerIds.filter((id) => id !== option.id);
                                    }
                                    const value =
                                      nextIds.length === 0
                                        ? ''
                                        : nextIds.join(',');
                                    updateFilter('partnerOwner', value);
                                  }}
                                />
                                <label
                                  htmlFor={`partner-${option.id}`}
                                  className="text-xs text-foreground cursor-pointer"
                                >
                                  {option.name}
                                </label>
                              </div>
                            );
                          })
                      ) : (
                        partnerOptions.length > 0 && partnerSearch && (
                          <p className="text-[11px] text-muted-foreground text-center py-2">No results found</p>
                        )
                      )}
                      {partnerOptions.length === 0 && (
                        <p className="text-[11px] text-muted-foreground">No partners available.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </FilterField>
          </div>

          <div className="space-y-3">
            <SectionHeader title="Classification" />
            <div className="grid grid-cols-2 gap-3">
              <FilterField label="Credit Rating">
                <div className="relative" ref={creditRatingDropdownRef}>
                  <button
                    type="button"
                    className={cn(
                      'w-full h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground',
                      'flex items-center justify-between gap-2',
                      'hover:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent'
                    )}
                    onClick={() => setIsCreditRatingDropdownOpen((prev) => !prev)}
                  >
                    <span className="truncate text-left">
                      {selectedCreditRatings.length === 0
                        ? 'Select Ratings'
                        : selectedCreditRatings.length === creditRatings.length
                          ? 'All Ratings selected'
                          : `${selectedCreditRatings.length} Rating(s) selected`}
                    </span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 text-muted-foreground shrink-0 transition-transform',
                        isCreditRatingDropdownOpen ? 'rotate-180' : 'rotate-0'
                      )}
                    />
                  </button>

                  {isCreditRatingDropdownOpen && (
                    <div className="absolute left-0 right-0 mt-1 border rounded-md p-2 bg-white shadow-md z-[60] space-y-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="selectAllCreditRatings"
                          checked={allCreditRatingsSelected}
                          onCheckedChange={(checked) => {
                            const isChecked = checked === true;
                            if (isChecked) {
                              updateFilter('creditRating', creditRatings.join(','));
                            } else {
                              updateFilter('creditRating', '');
                            }
                          }}
                        />
                        <label
                          htmlFor="selectAllCreditRatings"
                          className="text-xs text-muted-foreground cursor-pointer"
                        >
                          Select All
                        </label>
                      </div>

                      <div className="max-h-44 overflow-y-auto space-y-1 pr-1">
                        {creditRatings.map((rating) => {
                          const checked = selectedCreditRatings.includes(rating);
                          return (
                            <div key={rating} className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-muted/40">
                              <Checkbox
                                id={`rating-${rating}`}
                                checked={checked}
                                onCheckedChange={(checkedValue) => {
                                  const isChecked = checkedValue === true;
                                  let nextRatings = selectedCreditRatings;
                                  if (isChecked) {
                                    nextRatings = Array.from(new Set([...selectedCreditRatings, rating]));
                                  } else {
                                    nextRatings = selectedCreditRatings.filter((r) => r !== rating);
                                  }
                                  const value =
                                    nextRatings.length === 0
                                      ? ''
                                      : nextRatings.join(',');
                                  updateFilter('creditRating', value);
                                }}
                              />
                              <label
                                htmlFor={`rating-${rating}`}
                                className="text-xs text-foreground cursor-pointer"
                              >
                                {rating}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </FilterField>
              <FilterField label="Priority">
                <SearchableSelect
                  value={filters.priority}
                  onValueChange={(value) => handleSelectFilterChange('priority', value)}
                  options={priorities}
                  placeholder="All"
                  allLabel="All Priorities"
                />
              </FilterField>
            </div>

          </div>

          <div className="space-y-3">
            <SectionHeader title="Status" />
            {activeStage !== 'active-portfolio' && (
              <FilterField label="Status">
                <SearchableSelect
                  value={selectedStatusName}
                  onValueChange={(value) => {
                    if (value === 'all') {
                      updateFilter('pipelineStatus', '');
                      return;
                    }

                    const selectedStatus = statusOptions.find((status) => status.name === value);
                    updateFilter('pipelineStatus', selectedStatus ? String(selectedStatus.id) : '');
                  }}
                  options={statusNames}
                  placeholder="All"
                  allLabel="All Statuses"
                />
              </FilterField>
            )}
            <div className="grid grid-cols-2 gap-3">
              <FilterField label="MRA Status">
                <SearchableSelect value={filters.mraStatus} onValueChange={(value) => handleSelectFilterChange('mraStatus', value)} options={documentStatuses} placeholder="All" allLabel="All" />
              </FilterField>
              <FilterField label="TS Signed">
                <SearchableSelect value={filters.tsSignedStatus} onValueChange={(value) => handleSelectFilterChange('tsSignedStatus', value)} options={documentStatuses} placeholder="All" allLabel="All" />
              </FilterField>
            </div>
          </div>
        </div>

        <div className="px-5 py-3 border-t">
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={clearFilters}
              className="flex-1 h-10 rounded-md border border-gray-300 bg-white text-black hover:bg-gray-50"
            >
              Clear All
            </Button>

            <Button
              onClick={onClose}
              className="flex-1 h-10 rounded-md bg-[#3f8b98] hover:bg-[#367a85] text-white font-medium"
            >
              Apply
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
